'use client';

import React from 'react';

interface SemiCircleGaugeProps {
  /** Value from 0-100 */
  value: number;
  /** Size of the gauge in pixels */
  size?: number;
  /** Whether to show the value number */
  showValue?: boolean;
  /** Custom label below the value */
  label?: string;
  /** Variant style */
  variant?: 'default' | 'abstract';
  /** Additional class names */
  className?: string;
}

/**
 * Get color configuration based on Fear/Greed value
 */
export function getFearGreedColors(value: number) {
  if (value <= 25) return { 
    color: '#10b981', 
    text: 'text-emerald-400',
    label: 'Extreme Fear',
    shortLabel: 'Fear',
    bg: 'bg-emerald-900/20',
    border: 'border-emerald-800/50'
  };
  if (value <= 45) return { 
    color: '#22c55e', 
    text: 'text-green-400',
    label: 'Fear',
    shortLabel: 'Fear',
    bg: 'bg-green-900/20',
    border: 'border-green-800/50'
  };
  if (value <= 55) return { 
    color: '#eab308', 
    text: 'text-yellow-400',
    label: 'Neutral',
    shortLabel: 'Neutral',
    bg: 'bg-neutral-800/50',
    border: 'border-neutral-700/50'
  };
  if (value <= 75) return { 
    color: '#f97316', 
    text: 'text-orange-400',
    label: 'Greed',
    shortLabel: 'Greed',
    bg: 'bg-orange-900/20',
    border: 'border-orange-800/50'
  };
  return { 
    color: '#ef4444', 
    text: 'text-red-400',
    label: 'Extreme Greed',
    shortLabel: 'Greed',
    bg: 'bg-red-900/20',
    border: 'border-red-800/50'
  };
}

/**
 * CMC-style Semi-Circle "Speedometer" Gauge
 * 
 * Features:
 * - Gradient arc from green (fear) through yellow (neutral) to red (greed)
 * - Needle pointer indicating current value
 * - Value displayed prominently in center
 * - Classification label below
 */
export default function SemiCircleGauge({
  value,
  size = 120,
  showValue = true,
  label,
  variant = 'default',
  className = ''
}: SemiCircleGaugeProps) {
  // Clamp value between 0-100
  const clampedValue = Math.max(0, Math.min(100, value));
  
  // Calculate needle rotation (0 = left, 180 = right)
  // -90 degrees is left (0 value), 90 degrees is right (100 value)
  const needleRotation = -90 + (clampedValue / 100) * 180;
  
  // SVG dimensions
  const strokeWidth = size * 0.08;
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;
  
  // Calculate arc path for semi-circle
  const arcStartX = centerX - radius;
  const arcStartY = centerY;
  const arcEndX = centerX + radius;
  const arcEndY = centerY;
  
  // Gradient ID unique to this instance
  const gradientId = `gauge-gradient-${Math.random().toString(36).substr(2, 9)}`;
  
  const colors = getFearGreedColors(clampedValue);
  const isAbstract = variant === 'abstract';
  
  // Scale factors for different sizes
  const valueFontSize = size * 0.28;
  const labelFontSize = size * 0.08;
  const needleLength = radius * 0.75;
  const dotRadius = size * 0.04;

  return (
    <div className={`relative flex flex-col items-center ${className}`}>
      <svg 
        width={size} 
        height={size / 2 + strokeWidth} 
        viewBox={`0 0 ${size} ${size / 2 + strokeWidth}`}
        className="overflow-visible"
      >
        <defs>
          {/* Gradient for the arc - Fear (green) to Greed (red) */}
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="25%" stopColor="#22c55e" />
            <stop offset="50%" stopColor="#eab308" />
            <stop offset="75%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
          
          {/* Shadow filter for needle */}
          <filter id="needle-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.5"/>
          </filter>
        </defs>
        
        {/* Background arc (darker) */}
        <path
          d={`M ${arcStartX} ${centerY} A ${radius} ${radius} 0 0 1 ${arcEndX} ${centerY}`}
          fill="none"
          stroke="#262626"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Colored gradient arc */}
        <path
          d={`M ${arcStartX} ${centerY} A ${radius} ${radius} 0 0 1 ${arcEndX} ${centerY}`}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
        
        {/* Tick marks */}
        {[0, 25, 50, 75, 100].map((tick) => {
          const tickAngle = ((tick / 100) * 180 - 180) * (Math.PI / 180);
          const innerRadius = radius - strokeWidth / 2 - 2;
          const outerRadius = radius + strokeWidth / 2 + 2;
          const x1 = centerX + innerRadius * Math.cos(tickAngle);
          const y1 = centerY + innerRadius * Math.sin(tickAngle);
          const x2 = centerX + outerRadius * Math.cos(tickAngle);
          const y2 = centerY + outerRadius * Math.sin(tickAngle);
          
          return (
            <line
              key={tick}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#525252"
              strokeWidth={tick === 50 ? 2 : 1}
              strokeLinecap="round"
            />
          );
        })}
        
        {/* Needle */}
        <g 
          transform={`rotate(${needleRotation} ${centerX} ${centerY})`}
          className="transition-transform duration-700 ease-out"
          filter="url(#needle-shadow)"
        >
          {/* Needle body */}
          <line
            x1={centerX}
            y1={centerY}
            x2={centerX}
            y2={centerY - needleLength}
            stroke={isAbstract ? '#10b981' : colors.color}
            strokeWidth={2.5}
            strokeLinecap="round"
          />
          {/* Needle tip (triangle) */}
          <polygon
            points={`
              ${centerX},${centerY - needleLength - 4}
              ${centerX - 4},${centerY - needleLength + 6}
              ${centerX + 4},${centerY - needleLength + 6}
            `}
            fill={isAbstract ? '#10b981' : colors.color}
          />
        </g>
        
        {/* Center dot */}
        <circle
          cx={centerX}
          cy={centerY}
          r={dotRadius}
          fill={isAbstract ? '#10b981' : colors.color}
          className="transition-colors duration-300"
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={dotRadius * 0.5}
          fill="#0a0a0a"
        />
      </svg>
      
      {/* Value and label below gauge */}
      {showValue && (
        <div 
          className="flex flex-col items-center -mt-2"
          style={{ marginTop: -(size * 0.3) }}
        >
          <span 
            className={`font-mono font-bold ${colors.text} transition-colors duration-300`}
            style={{ fontSize: valueFontSize }}
          >
            {clampedValue}
          </span>
          {label && (
            <span 
              className="text-neutral-400 font-mono mt-0.5"
              style={{ fontSize: labelFontSize }}
            >
              {label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact inline version of the gauge for tight spaces
 */
export function CompactSemiCircleGauge({
  value,
  showLabel = true,
  className = ''
}: {
  value: number;
  showLabel?: boolean;
  className?: string;
}) {
  const clampedValue = Math.max(0, Math.min(100, value));
  const colors = getFearGreedColors(clampedValue);
  const needleRotation = -90 + (clampedValue / 100) * 180;
  
  const size = 48;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;
  
  const gradientId = `compact-gauge-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="relative">
        <svg 
          width={size} 
          height={size / 2 + 4} 
          viewBox={`0 0 ${size} ${size / 2 + 4}`}
          className="overflow-visible"
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#10b981" />
              <stop offset="25%" stopColor="#22c55e" />
              <stop offset="50%" stopColor="#eab308" />
              <stop offset="75%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
          </defs>
          
          {/* Background arc */}
          <path
            d={`M ${strokeWidth / 2} ${centerY} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${centerY}`}
            fill="none"
            stroke="#262626"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          
          {/* Colored arc */}
          <path
            d={`M ${strokeWidth / 2} ${centerY} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${centerY}`}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          
          {/* Needle */}
          <g 
            transform={`rotate(${needleRotation} ${centerX} ${centerY})`}
            className="transition-transform duration-500 ease-out"
          >
            <line
              x1={centerX}
              y1={centerY}
              x2={centerX}
              y2={centerY - radius * 0.65}
              stroke={colors.color}
              strokeWidth={2}
              strokeLinecap="round"
            />
          </g>
          
          {/* Center dot */}
          <circle cx={centerX} cy={centerY} r={3} fill={colors.color} />
          <circle cx={centerX} cy={centerY} r={1.5} fill="#0a0a0a" />
        </svg>
        
        {/* Value below */}
        <div 
          className="absolute left-1/2 -translate-x-1/2 text-center"
          style={{ bottom: '-2px' }}
        >
          <span className={`font-mono text-sm font-bold ${colors.text}`}>
            {clampedValue}
          </span>
        </div>
      </div>
      
      {showLabel && (
        <span className={`font-mono text-xs font-medium ${colors.text}`}>
          {colors.shortLabel}
        </span>
      )}
    </div>
  );
}
